import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, CircularProgress, Container, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import html2pdf from "html2pdf.js";
import { PURPLE, BORDER, SectionLabel, OrderSummary, cellSx } from "./OrderParts";
import { API_URL } from "../../redux/Constant";


const ViewOrder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const receiptRef = useRef(null);

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = () => {
    if (!receiptRef.current || downloading) return;
    setDownloading(true);
    html2pdf()
      .set({
        margin: 0,
        filename: `Order-BP${order?.order_id ?? id}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "pt", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["css", "legacy"], avoid: ["tr", ".pdf-avoid-break"] },
      })
      .from(receiptRef.current)
      .save()
      .finally(() => setDownloading(false));
  };

  useEffect(() => {
    if (!id) return;
    fetch(`${API_URL}/get/order/by/${id}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.status && data.items?.length) setOrder(data.items[0]);
        else setError(data.message || "Order not found");
      })
      .catch(() => setError("Failed to load order details"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress sx={{ color: PURPLE }} />
      </Box>
    );
  }

  if (error || !order) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
        <Box>
          <Typography sx={{ color: "#c62828", fontFamily: "ABLE", mb: 2 }}>
            {error || "Order not found"}
          </Typography>
          <Button
          type="button"
          onClick={() => navigate("/")}
          // startIcon={<ArrowBackIcon sx={{ fontSize: "1.2rem" }} />}
          sx={{
            background: "rgb(109 79 160) !important",
            color: "#fff !important",
            fontFamily: "ABLE !important",
            fontSize: "0.92rem !important",
            lineHeight: "initial !important",
            padding: "0.6rem 1rem !important",
            // width: "130px",
          }}
        >
          Go to Home
        </Button>
        </Box>
      </Box>
    );
  }

  const hasDeviceDetails = (order.device_details?.length ?? 0) > 0;

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .receipt-card { box-shadow: none !important; border-radius: 0 !important; margin: 0 !important; }
        }
      `}</style>

      <Box sx={{ minHeight: "100vh", background: "#f5f3fa", py: 4, px: 2 }}>
        <Container maxWidth="md" disableGutters>
          {/* Action bar */}
          <Box
            className="no-print"
            sx={{
              position: "relative",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
              mb: 2.5,
            }}
          >
            {/* Success banner, centered between the buttons */}
            <Box
              sx={{
                position: { sm: "absolute" },
                left: { sm: "50%" },
                transform: { sm: "translateX(-50%)" },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                width: { xs: "100%", sm: "auto" },
                order: { xs: -1, sm: 0 },
              }}
            >
              <CheckCircleIcon sx={{ color: "#2e7d32", fontSize: "26px" }} />
              <Typography
                sx={{ color: "#2e7d32", fontWeight: 700, fontSize: "18px", fontFamily: "ABLE", whiteSpace: "nowrap" }}
              >
                Order Placed Successfully
              </Typography>
            </Box>
            <Button
          type="button"
          onClick={() => navigate("/")}
          // startIcon={<ArrowBackIcon sx={{ fontSize: "1.2rem" }} />}
          sx={{
            background: "rgb(109 79 160) !important",
            color: "#fff !important",
            fontFamily: "ABLE !important",
            fontSize: "0.92rem !important",
            lineHeight: "initial !important",
            padding: "0.6rem 1rem !important",
            // width: "130px",
          }}
        >
          Go to Home
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                onClick={handleDownload}
                disabled={downloading}
                startIcon={<DownloadIcon sx={{ fontSize: "16px !important" }} />}
                sx={{
                  border: `2px solid ${BORDER}`,
                  color: PURPLE,
                  fontFamily: "ABLE",
                  px: 3,
                  textTransform: "none",
                  "&:hover": { background: "#f3eeff" },
                }}
              >
                {downloading ? "Preparing…" : "Download"}
              </Button>
              <Button
                onClick={() => window.print()}
                startIcon={<PrintIcon sx={{ fontSize: "16px !important" }} />}
                sx={{
                  background: `${PURPLE} !important`,
                  color: "#fff !important",
                  fontFamily: "ABLE",
                  px: 3,
                  textTransform: "none",
                }}
              >
                Print
              </Button>
            </Box>
          </Box>

          {/* Receipt card */}
          <Box ref={receiptRef} className="receipt-card" sx={{ background: "#fff", borderRadius: "12px", boxShadow: "rgb(146, 141, 191) 0px 2px 12px", overflow: "hidden" }}>
            {/* Header */}
            <Box sx={{ background: PURPLE, px: 4, py: 2.5 }}>
              <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "20px", fontFamily: "ABLE" }}>
                Order Confirmation
              </Typography>
              <Typography sx={{ color: "#f3eeff", fontSize: "14px", mt: 0.25, fontFamily: "ABLE" }}>
                Order #BP{order.order_id}
              </Typography>
            </Box>

            <Box sx={{ px: 4, py: 4, display: "flex", flexDirection: "column", gap: 3 }}>
              <Typography sx={{ color: "#555", fontSize: "14px", fontFamily: "ABLE", lineHeight: 1.7 }}>
                Thank you so much for your business. This confirms that we have received your order.
                Below you will find a summary of your purchase.
              </Typography>

              {/* Order Details, Addresses + Items */}
              <OrderSummary order={order} />

              {/* Shipping Information */}
              {order.shipping_method && (
                <Box className="pdf-avoid-break" sx={{ background: "#faf9fc", border: "1px solid #eee", borderRadius: "8px", p: 2.5 }}>
                  <SectionLabel>Shipping Information</SectionLabel>
                  <Typography sx={{ fontFamily: "ABLE", fontSize: "14px", color: "#444", mb: 1 }}>
                    <strong>Method:</strong> {order.shipping_method}
                  </Typography>
                  <Typography sx={{ fontFamily: "ABLE", fontSize: "13px", color: "#555", lineHeight: 1.7 }}>
                    Your order will be shipped within two business days. You will receive an email
                    with tracking information once we have shipped.
                  </Typography>
                </Box>
              )}

              {/* Device Registration */}
              {hasDeviceDetails && (
                <Box className="pdf-avoid-break">
                  <SectionLabel>Device Registration Information</SectionLabel>
                  <Box sx={{ border: "1px solid #eee", borderRadius: "8px", overflow: "hidden" }}>
                    <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                      <Box component="thead" sx={{ background: "#faf9fc" }}>
                        <Box component="tr">
                          <Box component="th" sx={{ ...cellSx, textAlign: "left", color: "#666", fontWeight: 700, borderRight: "1px solid #eee" }}>Product Name</Box>
                          <Box component="th" sx={{ ...cellSx, textAlign: "left", color: "#666", fontWeight: 700, borderRight: "1px solid #eee" }}>Modules</Box>
                          <Box component="th" sx={{ ...cellSx, textAlign: "left", color: "#666", fontWeight: 700 }}>Device ID</Box>
                        </Box>
                      </Box>
                      <Box component="tbody">
                        {order.device_details.map((d, i) => (
                          <Box component="tr" key={i} sx={{ borderTop: "1px solid #f0f0f0" }}>
                            <Box component="td" sx={{ ...cellSx, color: "#000", borderRight: "1px solid #eee" }}>{d.product_name || "—"}</Box>
                            <Box component="td" sx={{ ...cellSx, color: "#555", borderRight: "1px solid #eee" }}>{d.module_names || "—"}</Box>
                            <Box component="td" sx={{ ...cellSx, color: "#000", fontFamily: "monospace" }}>{d.unique_order || "—"}</Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Transaction ID */}
              {order.transaction_id && (
                <Box className="pdf-avoid-break" sx={{ background: "#faf9fc", border: "1px solid #eee", borderRadius: "8px", p: 2.5 }}>
                  <SectionLabel>Payment Information</SectionLabel>
                  <Typography sx={{ fontFamily: "ABLE", fontSize: "14px" }}>
                    <span style={{ color: "#999" }}>Transaction ID: </span>
                    <span style={{ fontWeight: 600, color: "#000", wordBreak: "break-all" }}>{order.transaction_id}</span>
                  </Typography>
                </Box>
              )}

              {/* Contact & Support */}
              <Box className="pdf-avoid-break" sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                <Box sx={{ background: "#faf9fc", border: "1px solid #eee", borderRadius: "8px", p: 2.5 }}>
                  <SectionLabel>Contact Information</SectionLabel>
                  <Typography sx={{ fontFamily: "ABLE", fontSize: "13px", color: "#555", lineHeight: 1.7 }}>
                    If you have any questions or concerns regarding your order, please contact our
                    customer support team at{" "}
                    <a href="mailto:admin@betterphysiology.com" style={{ color: PURPLE }}>
                      admin@betterphysiology.com
                    </a>{" "}
                    or call us at{" "}
                    <a href="tel:+18008509795" style={{ color: PURPLE }}>
                      +1.800.850.9795
                    </a>.
                  </Typography>
                </Box>
                <Box sx={{ background: "#faf9fc", border: "1px solid #eee", borderRadius: "8px", p: 2.5 }}>
                  <SectionLabel>Tech Support</SectionLabel>
                  <Typography sx={{ fontFamily: "ABLE", fontSize: "13px", color: "#555", lineHeight: 1.7 }}>
                    For initial tech support regarding software download problems and/or hardware
                    issues, email{" "}
                    <a href="mailto:tech@betterphysiology.com" style={{ color: PURPLE }}>
                      tech@betterphysiology.com
                    </a>{" "}
                    for immediate live Zoom support.
                  </Typography>
                </Box>
              </Box>

              {/* Sign-off */}
              <Box className="pdf-avoid-break" sx={{ borderTop: "1px solid #eee", pt: 3, fontFamily: "ABLE", fontSize: "13px", color: "#555", lineHeight: 1.8 }}>
                <Typography sx={{ mb: 1.5, fontFamily: "ABLE", fontSize: "13px" }}>
                  Thank you for choosing Better Physiology Ltd. We appreciate your business!
                </Typography>
                <Typography sx={{ fontFamily: "ABLE", fontSize: "13px" }}>
                  Sincerely,<br />
                  <strong style={{ color: "#000" }}>Customer Service</strong><br />
                  Better Physiology Ltd.<br />
                  Santa Fe, NM&nbsp;&nbsp;USA<br />
                  Tel, Admin: 307.773.0888<br />
                  Tel, Sales: 505.820.3376<br />
                  Fax: 505.213.0351
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Bottom download / print buttons */}
          <Box className="no-print" sx={{ textAlign: "center", mt: 3, display: "flex", justifyContent: "center", gap: 1.5 }}>
            <Button
              onClick={handleDownload}
              disabled={downloading}
              startIcon={<DownloadIcon sx={{ fontSize: "16px !important" }} />}
              sx={{
                border: `2px solid ${BORDER}`,
                color: PURPLE,
                fontFamily: "ABLE",
                px: 3,
                py: 1,
                textTransform: "none",
                "&:hover": { background: "#f3eeff" },
              }}
            >
              {downloading ? "Preparing…" : "Download"}
            </Button>
            <Button
              onClick={() => window.print()}
              startIcon={<PrintIcon sx={{ fontSize: "16px !important" }} />}
              sx={{
                background: `${PURPLE} !important`,
                color: "#fff !important",
                fontFamily: "ABLE",
                px: 3,
                py: 1,
                textTransform: "none",
              }}
            >
              Print
            </Button>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default ViewOrder;
